import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import DocumentsPicker from "@/components/DocumentsPicker";
import GoogleButton from "@/components/GoogleButton";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { PillBottle, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const google = useLocation().state?.google;
  const [role, setRole] = useState("pharmacist");
  const [form, setForm] = useState(google ? { name: google.name } : {});
  const [docs, setDocs] = useState([]);
  const [docWarn, setDocWarn] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (docs.length === 0) return setError("Ajoutez au moins une pièce justificative (carte professionnelle, patente, RNE…)");
    setLoading(true);
    try {
      const payload = google ? { ...form, email: google.email, google_signup_token: google.signup_token, role } : { ...form, role };
      const res = await register(payload);
      let failed = 0;
      for (const d of docs) {
        const fd = new FormData();
        fd.append("token", res.upload_token);
        fd.append("doc_type", d.doc_type);
        fd.append("file", d.file);
        try { await api.post("/auth/register/documents", fd); } catch { failed += 1; }
      }
      if (failed) setDocWarn(`${failed} pièce(s) n'ont pas pu être envoyées. L'administrateur pourra vous les redemander.`);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 rounded-2xl p-10" data-testid="register-success">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
          <h1 className="mt-5 text-2xl font-extrabold text-slate-900">Compte créé !</h1>
          <p className="mt-3 text-slate-500 font-medium">Votre inscription et vos pièces justificatives sont en attente de validation par l'administrateur. Vous serez notifié dès l'activation de votre compte.</p>
          {docWarn && <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="register-doc-warning">{docWarn}</p>}
          <Link to="/login" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">
            Aller à la connexion <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  const input = "mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="h-9 w-9 rounded-lg bg-indigo-600 grid place-items-center"><PillBottle className="h-5 w-5 text-white" /></div>
          <span className="font-extrabold text-slate-900">PharmaGroup</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Créer un compte</h1>
          <p className="text-slate-500 font-medium mt-1">Rejoignez la plateforme B2B pharmaceutique.</p>

          <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg">
            {[["pharmacist", "Pharmacien"], ["supplier", "Fournisseur"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setRole(v)} data-testid={`role-${v}`}
                className={`h-10 rounded-md text-sm font-semibold transition-colors duration-200 ${role === v ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Nom complet</label>
              <input data-testid="reg-name" required value={form.name || ""} onChange={set("name")} className={input} placeholder="Dr. Prénom Nom" />
            </div>
            {role === "pharmacist" ? (
              <div>
                <label className="text-sm font-semibold text-slate-700">Nom de la pharmacie</label>
                <input data-testid="reg-pharmacy" onChange={set("pharmacy_name")} className={input} placeholder="Pharmacie ..." />
              </div>
            ) : (
              <div>
                <label className="text-sm font-semibold text-slate-700">Nom de la société</label>
                <input data-testid="reg-company" onChange={set("company_name")} className={input} placeholder="Laboratoire / Distributeur" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700">Ville</label>
                <input onChange={set("city")} className={input} placeholder="Tunis" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Téléphone</label>
                <input onChange={set("phone")} className={input} placeholder="+216 ..." />
              </div>
            </div>
            {google ? (
              <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3.5 py-2.5 text-sm" data-testid="reg-google-email">
                Inscription avec Google : <span className="font-semibold text-indigo-700">{google.email}</span>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Email</label>
                  <input data-testid="reg-email" type="email" required onChange={set("email")} className={input} placeholder="vous@exemple.tn" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Mot de passe</label>
                  <input data-testid="reg-password" type="password" required minLength={6} onChange={set("password")} className={input} placeholder="••••••••" />
                </div>
              </>
            )}
            <DocumentsPicker docs={docs} setDocs={(d) => { setDocs(d); if (d.length) setError(""); }} />
            {error && <div data-testid="register-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="register-submit" disabled={loading} className="w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Créer mon compte <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          {!google && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs font-semibold text-slate-400"><div className="h-px bg-slate-200 flex-1" />OU<div className="h-px bg-slate-200 flex-1" /></div>
              <GoogleButton label="S'inscrire avec Google" />
            </>
          )}
          <p className="mt-5 text-sm text-slate-500 text-center">Déjà inscrit ? <Link to="/login" className="font-semibold text-indigo-600 hover:underline">Connexion</Link></p>
        </div>
      </div>
    </div>
  );
}
